/**
 * Pure helpers for parsing motor subjects into a stable motor identity + a
 * human-friendly display name. Extracted from the worker so they can be unit
 * tested in isolation (no worker/global state).
 *
 * Two dotted schemas are supported by {@link deriveMotorIdNewFormat}:
 *
 *  - Schema A (FOCKE): the axis is the LAST path token and all of a motor's
 *    signals sit directly under it, e.g.
 *      …EDGE1.22622.P0655.F.ZIFO.MA12.TorqueRmsValue        → "P0655_F_ZIFO_MA12"
 *
 *  - Schema B (edge-16): the axis is a MIDDLE token (`fbAxis_MAxx[_Device]`,
 *    or an operating-hours `__FB…_MAxx` wrapper), and a single motor's signals
 *    are spread across sub-groups (IoAxis_stState / AX8000_stState /
 *    fbOperatingHoursData). One component can hold several axes, e.g.
 *      …21304.P0655_F_ZFSB.fbCigFormation.fbAxis_MA03_Device.IoAxis_stState.TorqueRmsValue
 *      …21304.P0655_F_ZFSB.fbCigFormation.fbAxis_MA05_Device.IoAxis_stState.TorqueRmsValue
 *    which must stay distinct → grouped by <module>_<component>_<axis>:
 *      "P0655_F_ZFSB_fbCigFormation_MA03", "…_MA05".
 */

/**
 * Build a motorId from a new-format (dotted) subject.
 * @param subject               full NATS subject
 * @param basePrefixTokenCount  number of leading tokens before the motor path
 *                              (e.g. 4 for `edge-data.live.*.<serial>`)
 * @returns the motorId, or null when the subject carries no motor axis / is not
 *          a `_F_` module signal.
 */
export function deriveMotorIdNewFormat(
  subject: string,
  basePrefixTokenCount: number,
): string | null {
  const tokens = subject.split('.');
  const pathTokens = tokens.slice(basePrefixTokenCount, -1);
  if (pathTokens.length === 0) return null;

  let axisIdx = -1;
  let axis = '';
  for (let i = 0; i < pathTokens.length; i++) {
    const m = pathTokens[i].match(/MA\d+/);
    if (m) {
      axisIdx = i;
      axis = m[0];
      break;
    }
  }

  let motorId: string;
  if (axisIdx === -1) {
    // No motor axis (MAxx) → not a real motor: virtual-master (VMxx), encoder,
    // or infrastructure signals. Drop it so the motor list stays clean.
    return null;
  } else if (axisIdx < pathTokens.length - 1) {
    // Schema B: axis is a middle token; unify all sub-groups under the axis by
    // grouping on <tokens-before-axis> + axis number.
    motorId = [...pathTokens.slice(0, axisIdx), axis].join('_');
  } else {
    // Schema A / axis-last: motor = full path (axis already included).
    motorId = pathTokens.join('_');
  }

  if (!motorId.includes('_F_')) return null;
  return motorId;
}

/**
 * Derive a human-friendly motor label from a motorId.
 *   "P0655_F_ZIFO_MA12"                         → "ZIFO MA12"      (schema A)
 *   "P0655_F_ZFSB_fbCigFormation_MA03"          → "CigFormation (MA03)" (schema B)
 */
export function extractShortName(motorId: string): string {
  const axis = motorId.match(/MA\d+/)?.[0] ?? '';
  // Schema B: descriptive function block token ("fbCigFormation" → "CigFormation";
  // skip the axis wrapper "fbAxis_..._Device").
  const fn = motorId.match(/fb(?!Axis)([A-Za-z0-9]+)/)?.[1] ?? '';
  if (fn && axis) return `${fn} (${axis})`;
  if (fn) return fn;
  // Schema A: the component token sits between "_F_" and the axis
  // ("P0655_F_ZFSB_MA02" → component "ZFSB"). Include it so motors that share an
  // axis number across modules stay distinguishable.
  const comp = motorId.match(/_F_([A-Za-z0-9]+)_MA\d+/)?.[1];
  if (comp && axis) return `${comp} ${axis}`;
  if (axis) return axis;
  const parts = motorId.split('_');
  return parts[parts.length - 1] || motorId;
}
